import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { useHelpSearch } from '../api/queries'

const DEBOUNCE_MS = 200

/**
 * Top-of-page search input. Debounces the query, hits /api/help/search,
 * renders an inline hit list. `/` keyboard shortcut focuses the input
 * (when nothing else has focus AND we're inside /help).
 */
export function HelpSearchBar() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  // `/` global focus
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== '/') return
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea') return
      e.preventDefault()
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const { data, isLoading } = useHelpSearch(debouncedQuery, open && query.length >= 2)

  const hits = data?.hits ?? []
  const showResults = open && query.length >= 2

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-sm border border-border-default bg-surface px-2.5 py-1 focus-within:border-accent">
        <Search size={14} className="text-tertiary" />
        <input
          ref={inputRef}
          type="search"
          placeholder="Search help…  (press / to focus)"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="flex-1 border-0 bg-transparent text-sm text-primary outline-none placeholder:text-tertiary"
        />
        {query ? (
          <button
            type="button"
            onClick={() => { setQuery(''); inputRef.current?.focus() }}
            aria-label="Clear search"
            className="text-tertiary hover:text-primary"
          >
            <X size={12} />
          </button>
        ) : null}
      </div>

      {showResults ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[60vh] overflow-y-auto rounded-sm border border-border-default bg-surface shadow-lg">
          {isLoading ? (
            <div className="px-3 py-2 text-xs text-tertiary">Searching…</div>
          ) : hits.length === 0 ? (
            <div className="px-3 py-2 text-xs text-tertiary">
              No matches for <span className="font-semibold text-primary">{query}</span>
            </div>
          ) : (
            hits.map((hit) => (
              <a
                key={`${hit.url}-${hit.title}`}
                href={hit.url}
                onMouseDown={(e) => e.preventDefault()}
                className={cn(
                  'block border-b border-border-subtle px-3 py-2 text-sm hover:bg-surface-hover',
                  'last:border-b-0',
                )}
              >
                <div className="font-semibold text-primary">{hit.title}</div>
                <div className="mt-0.5 truncate text-2xs text-tertiary">{hit.url}</div>
                {hit.snippet ? (
                  <div className="mt-1 line-clamp-2 text-xs text-secondary">{hit.snippet}</div>
                ) : null}
              </a>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
